import React from 'react';
import { ArrowRight } from 'lucide-react';
import { jobOpenings } from '../data/mock';
import useScrollAnimation from '../hooks/useScrollAnimation';
import { useModal } from '../context/ModalContext';

const Careers = ({ showViewAll = true }) => {
  const [sectionRef, isVisible] = useScrollAnimation({ threshold: 0.1 });
  const { openDemoModal } = useModal();

  return (
    <section id="careers" className="relative py-20 sm:py-24 lg:py-32 bg-[--bg-primary]">
      <div className="absolute top-0 left-0 right-0 section-divider" />
      
      <div ref={sectionRef} className="max-w-[1500px] mx-auto px-4 sm:px-6">
        {/* Section Header */}
        <div 
          className={`mb-12 max-w-5xl transition-all duration-700 ease-out ${
            isVisible ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'
          }`}
        >
          <p className="section-eyebrow mb-4">Job Openings</p>
          <h2 className="text-[clamp(2rem,4.1vw,3.6rem)] font-semibold text-[--text-primary] leading-[1.02]">
            Join the team building
            <br />
            <span className="accent-wash">practical AI products.</span>
          </h2>
          {showViewAll && (
            <a 
              href="/careers"
              className="mt-7 inline-flex items-center gap-2 text-[--accent] text-[11px] font-medium uppercase tracking-[0.18em] font-mono hover:gap-3 transition-all"
            >
              See all
              <ArrowRight className="w-4 h-4" />
            </a>
          )}
        </div>

        {/* Job Listings */}
        <div className="space-y-2">
          {jobOpenings.map((job, index) => (
            <div
              key={job.id}
              role="button"
              tabIndex={0}
              onClick={openDemoModal}
              onKeyDown={(e) => e.key === 'Enter' && openDemoModal()}
              className={`group cursor-pointer flex flex-col sm:flex-row sm:items-center justify-between p-6 rounded-2xl soft-card hover:border-[--border-hover] hover:bg-[--bg-secondary] transition-all duration-500 ${
                isVisible ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'
              }`}
              style={{ transitionDelay: `${200 + index * 75}ms` }}
            >
              <div className="flex items-center gap-4 mb-4 sm:mb-0">
                {/* Tags */}
                <div className="flex items-center gap-2">
                  {job.isNew && (
                    <span className="text-[10px] font-mono text-[--bg-primary] bg-[--accent] px-2 py-0.5 rounded-full uppercase tracking-[0.1em]">
                      New
                    </span>
                  )}
                  <span className="text-[11px] font-mono text-[--text-muted] uppercase tracking-[0.16em]">
                    {job.department}
                  </span>
                </div>
              </div>

              <div className="flex-1 sm:px-6">
                <h3 className="text-base font-medium text-[--text-primary] group-hover:text-[--accent] transition-colors">
                  {job.title}
                </h3>
              </div>

              <div className="flex flex-wrap items-center gap-3 sm:gap-6 mt-4 sm:mt-0">
                <span className="text-xs sm:text-sm text-[--text-muted]">
                  {job.location} · {job.type}
                </span>
                <span className="inline-flex items-center gap-2 text-[--text-secondary] text-[11px] font-mono uppercase tracking-[0.16em] group-hover:text-[--accent] group-hover:gap-3 transition-all">
                  Apply
                  <ArrowRight className="w-4 h-4" />
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Careers;
